import React, { useState, useEffect } from "react";
import { Typography, Chip } from "@mhome/ui";
import { alpha } from "@mui/material/styles";
import { calculateGridPosition } from "./grid-position-utils";

const TempStack = ({
  stackedItems = [],
  onDragFromStack,
  isDragOver = false,
  onChipDragEnd,
  getCardName,
  onMobileDragOver,
}) => {
  const [draggedChip, setDraggedChip] = useState(null);
  const [touchDragData, setTouchDragData] = useState(null);

  const handleChipClick = (item) => {
    // Prevent click if we just finished dragging
    if (draggedChip === item.i) {
      setDraggedChip(null);
      return;
    }

    // Click functionality disabled - items must be dragged to remove from stack
    // No auto-remove on click
  };

  const handleChipDragStart = (e, item) => {
    // Set drag data for the chip
    e.dataTransfer.setData(
      "text/plain",
      JSON.stringify({
        type: "stackItem",
        item: item,
      })
    );
    e.dataTransfer.effectAllowed = "move";

    // Mark this chip as being dragged
    setDraggedChip(item.i);

    // Store the item being dragged for reference
    window.draggedStackItem = item;
  };

  const handleChipDragEnd = (e) => {
    // Clean up
    window.draggedStackItem = null;

    // Call parent callback to clear preview
    if (onChipDragEnd) {
      onChipDragEnd();
    }

    // Reset dragged chip after a short delay to prevent click
    setTimeout(() => {
      setDraggedChip(null);
    }, 100);
  };

  // Touch event handlers for mobile support
  const handleTouchStart = (e, item) => {
    const touch = e.touches[0];

    setTouchDragData({
      item: item,
      startX: touch.clientX,
      startY: touch.clientY,
      isDragging: false,
    });
  };

  const handleTouchMove = (e) => {
    if (!touchDragData) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchDragData.startX);
    const deltaY = Math.abs(touch.clientY - touchDragData.startY);

    // Start dragging if moved enough
    if (!touchDragData.isDragging && (deltaX > 10 || deltaY > 10)) {
      console.debug("Starting touch drag for item:", touchDragData.item.i);
      setTouchDragData((prev) => ({ ...prev, isDragging: true }));
      setDraggedChip(touchDragData.item.i);
      window.draggedStackItem = touchDragData.item;
    }

    // If we're dragging and have the mobile drag over handler, call it
    if (touchDragData.isDragging && onMobileDragOver) {
      const stackContainer = document.querySelector(".temp-stack-container");

      if (stackContainer) {
        const stackRect = stackContainer.getBoundingClientRect();

        // Check if dragging to the right of stack area (vertical layout)
        if (touch.clientX > stackRect.right) {
          // Create synthetic event for mobile drag over
          const syntheticEvent = {
            clientX: touch.clientX,
            clientY: touch.clientY,
            preventDefault: () => {},
            type: "touchmove",
            dataTransfer: {
              dropEffect: "move",
              setData: () => {},
              getData: () => {},
            },
          };

          onMobileDragOver(syntheticEvent);
        }
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (!touchDragData) return;

    const touch = e.changedTouches[0];

    if (touchDragData.isDragging) {
      // Check if dropped over grid area
      const stackContainer = document.querySelector(".temp-stack-container");

      if (stackContainer) {
        const stackRect = stackContainer.getBoundingClientRect();

        console.debug("Touch end debug:", {
          touchX: touch.clientX,
          touchY: touch.clientY,
          stackRect: {
            left: stackRect.left,
            right: stackRect.right,
            top: stackRect.top,
            bottom: stackRect.bottom,
          },
          isDragging: touchDragData.isDragging,
          isRightOfStack: touch.clientX > stackRect.right,
        });

        // Check if dropped to the right of stack area (vertical layout)
        if (touch.clientX > stackRect.right) {
          // Use the existing handleGridDrop logic by creating synthetic event
          if (onDragFromStack) {
            // The grid layout is in a sibling container, not a child of the stack container
            // We need to go up to the common parent and then find the grid layout
            const stackParent = stackContainer.parentElement;
            if (!stackParent) {
              return;
            }

            let gridLayoutElement =
              stackParent.querySelector(".react-grid-layout");

            // If not found, try searching in sibling containers
            if (!gridLayoutElement) {
              const siblings = Array.from(stackParent.children);

              for (const sibling of siblings) {
                const foundGrid = sibling.querySelector(".react-grid-layout");
                if (foundGrid) {
                  gridLayoutElement = foundGrid;
                  break;
                }
              }
            }

            if (!gridLayoutElement) {
              gridLayoutElement = document.querySelector(".react-grid-layout");
            }

            if (!gridLayoutElement) {
              return;
            }

            // Use shared grid position calculation logic
            const gridPosition = calculateGridPosition(
              touch.clientX,
              touch.clientY,
              stackParent,
              null, // No layouts needed for simple position calc
              false // Chat sidebar not considered in mobile
            );

            const finalX = gridPosition.x;
            const finalY = gridPosition.y;

            console.debug("Mobile position calculation:", {
              touchPos: { x: touch.clientX, y: touch.clientY },
              finalPos: { x: finalX, y: finalY },
              item: {
                id: touchDragData.item.i,
                w: touchDragData.item.w,
                h: touchDragData.item.h,
              },
            });

            onDragFromStack(touchDragData.item, null, {
              x: touch.clientX,
              y: touch.clientY,
            });
          }
        }
      }
    }

    // Cleanup
    setTouchDragData(null);
    window.draggedStackItem = null;

    if (onChipDragEnd) {
      onChipDragEnd();
    }

    setTimeout(() => {
      setDraggedChip(null);
    }, 100);
  };

  // Add global touch move handler
  useEffect(() => {
    if (touchDragData) {
      const handleGlobalTouchMove = (e) => {
        handleTouchMove(e);
      };

      document.addEventListener("touchmove", handleGlobalTouchMove, {
        passive: false,
      });

      return () => {
        document.removeEventListener("touchmove", handleGlobalTouchMove);
      };
    }
  }, [touchDragData]);

  return (
    <div
      className="temp-stack-container flex flex-col h-full w-full bg-transparent border-2 border-dashed transition-all duration-200 gap-2 overflow-auto"
      style={{
        borderColor: isDragOver ? "hsl(var(--primary))" : "hsl(var(--border))",
      }}
    >
      {stackedItems.length > 0 ? (
        <div className="flex flex-col gap-1 flex-1">
          {stackedItems.map((item, index) => {
            const displayName = getCardName
              ? getCardName(item.cardData)
              : item.title || `W${index + 1}`;
            return (
              <Chip
                key={`${item.i}-${index}`}
                label={displayName}
                size="small"
                draggable
                onClick={() => handleChipClick(item)}
                onDragStart={(e) => handleChipDragStart(e, item)}
                onDragEnd={handleChipDragEnd}
                onTouchStart={(e) => handleTouchStart(e, item)}
                onTouchEnd={handleTouchEnd}
                className="cursor-grab w-full min-h-[28px] h-auto text-[0.65rem] p-[2px] select-none transition-all duration-150 active:cursor-grabbing hover:scale-[1.02] [&>span]:overflow-hidden [&>span]:text-ellipsis [&>span]:whitespace-normal [&>span]:line-clamp-3 [&>span]:leading-[1.2] [&>span]:p-[2px_4px] [&>span]:text-[0.6rem] [&>span]:break-words"
                style={{
                  backgroundColor: "hsl(var(--primary) / 0.12)",
                  color: "hsl(var(--primary))",
                  border: "1px solid",
                  borderColor: "hsl(var(--primary) / 0.3)",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  MozUserSelect: "none",
                  msUserSelect: "none",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "hsl(var(--primary) / 0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "hsl(var(--primary) / 0.12)";
                }}
              />
            );
          })}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-center">
          <Typography
            variant="caption"
            className="italic text-sm leading-[1.2] -rotate-90 whitespace-nowrap"
            style={{
              color: isDragOver
                ? "hsl(var(--primary))"
                : "hsl(var(--muted-foreground))",
              fontWeight: isDragOver ? 600 : 500,
            }}
          >
            {isDragOver ? "Drop here" : "Drag to temporary store"}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default TempStack;
